import React, { useState, useRef, useEffect, useCallback } from 'react';
import fetchWithRetry from '../utils/fetchWithRetry';

const API_BASE = process.env.REACT_APP_API_URL ||
  (window.location.port === '3000' ? `http://${window.location.hostname}:3001` : '');

const STEPS = [
  { id: 'welcome', title: 'Welcome' },
  { id: 'phone', title: 'Phone Setup' },
  { id: 'crm', title: 'CRM (Optional)' },
  { id: 'done', title: 'Ready' },
];

export default function OnboardingWizard({ user, token, onComplete, onSkip }) {
  const cardRef = useRef(null);
  useEffect(() => { if (cardRef.current) cardRef.current.classList.add('mppc-active'); }, []);

  const [step, setStep] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [forwardNumber, setForwardNumber] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [verifying, setVerifying] = useState(false);
  const [validationCode, setValidationCode] = useState('');
  const [verified, setVerified] = useState(false);
  const [platformNumber, setPlatformNumber] = useState('');

  const [crmApiKey, setCrmApiKey] = useState('');
  const [crmLocationId, setCrmLocationId] = useState('');
  const [crmError, setCrmError] = useState('');
  const [crmSaving, setCrmSaving] = useState(false);

  const handleVerifyCallerId = async () => {
    if (!phoneNumber.trim()) {
      setError('Please enter your business phone number.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const res = await fetchWithRetry(`${API_BASE}/api/auth/verify-caller-id`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phoneNumber }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || `Failed to start verification (${res.status})`);
        setLoading(false);
        return;
      }
      if (data.alreadyVerified) {
        setVerified(true);
        setLoading(false);
        return;
      }
      setValidationCode(data.validationCode);
      setVerifying(true);
      setLoading(false);
    } catch (err) {
      setError(`Could not connect to server: ${err.message}`);
      setLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    setLoading(true);
    setError('');
    try {
      const statusRes = await fetchWithRetry(`${API_BASE}/api/auth/caller-id-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!statusRes.ok) { setError(`Server error (${statusRes.status})`); setLoading(false); return; }
      const statusData = await statusRes.json().catch(() => ({}));
      if (statusData.verified) {
        setVerified(true);
        setVerifying(false);
        if (statusData.platformNumber) setPlatformNumber(statusData.platformNumber);
      } else {
        setError('Not verified yet. Make sure you answered the call and entered the code on your keypad.');
      }
    } catch (err) { setError(`Could not connect to server: ${err.message}`); }
    setLoading(false);
  };

  const handlePhoneSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!verified) {
      setError('Please verify your phone number first.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const res = await fetchWithRetry(`${API_BASE}/api/auth/twilio-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phoneNumber, forwardNumber }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || `Failed to save (${res.status})`);
        setLoading(false);
        return;
      }
      if (data.platformNumber) setPlatformNumber(data.platformNumber);
      if (data.provisionError) {
        setError(`Phone saved, but coaching line provisioning failed: ${data.provisionError}. You can retry in Settings.`);
      }
      setStep(2);
    } catch (err) {
      setError(`Could not connect to server: ${err.message}`);
    }
    setLoading(false);
  }, [verified, phoneNumber, forwardNumber, token]);

  const handleCrmSubmit = async (e) => {
    e.preventDefault();
    setCrmError('');
    setCrmSaving(true);
    try {
      const res = await fetchWithRetry(`${API_BASE}/api/auth/crm-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ apiKey: crmApiKey, locationId: crmLocationId, pushSummary: true, pushTags: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setCrmError(data.error || `Connection failed (${res.status})`); setCrmSaving(false); return; }
      setStep(3);
    } catch (err) { setCrmError(`Could not connect to server: ${err.message}`); }
    setCrmSaving(false);
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-card" ref={cardRef}>
        <div className="onboarding-progress">
          {STEPS.map((s, i) => (
            <div key={s.id} className={`onboarding-step ${i <= step ? 'step-active' : ''}`}>
              <div className="step-dot">{i < step ? '\u2713' : i + 1}</div>
              <span className="step-label">{s.title}</span>
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className="onboarding-body">
            <h2>Welcome, {user?.name || 'there'}!</h2>
            <p>Let's get your AI call coach set up. We just need your phone number to get started — everything else is handled by the platform.</p>
            <p>It only takes about a minute.</p>
            <button className="onboarding-btn" onClick={() => setStep(1)}>
              Let's Go
            </button>
            {onSkip && (
              <button
                type="button"
                onClick={onSkip}
                style={{
                  display: 'block', margin: '12px auto 0', background: 'none',
                  border: 'none', color: 'var(--mppc-text-body)', cursor: 'pointer',
                  fontSize: 14, textDecoration: 'underline',
                }}
              >
                Skip — I'll set this up later in Settings
              </button>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="onboarding-body">
            <h2>Connect Your Phone</h2>
            <p style={{ marginBottom: 8 }}>We'll verify your business number so it shows as your caller ID on outbound calls.</p>

            <form onSubmit={handlePhoneSubmit} className="onboarding-form">
              <fieldset style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '12px 16px', margin: '0 0 12px' }}>
                <legend style={{ color: '#94a3b8', fontSize: 13, padding: '0 6px' }}>Your Business Number</legend>
                <label>
                  Phone Number
                  <input
                    type="tel"
                    placeholder="+1 (555) 123-4567 or +44 20 7946 0958"
                    value={phoneNumber}
                    onChange={(e) => { setPhoneNumber(e.target.value); setVerified(false); setVerifying(false); setValidationCode(''); }}
                    required
                    className="onboarding-input"
                    disabled={verifying}
                  />
                  <small style={{ color: '#94a3b8', fontSize: 11 }}>
                    Your business number — shows as your caller ID. Include country code for international numbers.
                  </small>
                </label>

                {!verified && !verifying && (
                  <button
                    type="button"
                    onClick={handleVerifyCallerId}
                    disabled={loading || !phoneNumber.trim()}
                    style={{
                      marginTop: 10, padding: '8px 20px', borderRadius: 6,
                      border: '1px solid rgba(99,102,241,0.4)', background: 'rgba(99,102,241,0.15)',
                      color: '#a5b4fc', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      opacity: (loading || !phoneNumber.trim()) ? 0.5 : 1,
                    }}
                  >
                    {loading ? 'Starting...' : 'Verify My Number'}
                  </button>
                )}

                {verifying && validationCode && (
                  <div style={{
                    marginTop: 12, padding: '14px 16px', background: 'rgba(99,102,241,0.1)',
                    border: '1px solid rgba(99,102,241,0.25)', borderRadius: 8,
                  }}>
                    <div style={{ color: '#a5b4fc', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                      We're calling {phoneNumber} now
                    </div>
                    <div style={{ color: '#cbd5e1', fontSize: 13, marginBottom: 8 }}>
                      Answer the call and enter this code on your keypad:
                    </div>
                    <div style={{
                      fontSize: 32, fontWeight: 700, letterSpacing: 6, textAlign: 'center',
                      color: '#f1f5f9', fontFamily: 'monospace', padding: '8px 0',
                    }}>
                      {validationCode}
                    </div>
                    <button
                      type="button"
                      onClick={handleCheckVerification}
                      disabled={loading}
                      style={{
                        marginTop: 12, width: '100%', padding: '10px 20px', borderRadius: 6,
                        border: 'none', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        color: '#fff', fontSize: 14, fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
                        opacity: loading ? 0.7 : 1,
                      }}>
                      {loading ? 'Checking...' : "I've Entered the Code"}
                    </button>
                    <div style={{ fontSize: 11, color: '#64748b', textAlign: 'center', marginTop: 8 }}>
                      You have up to 10 minutes to complete verification.
                    </div>
                  </div>
                )}

                {verified && (
                  <div style={{
                    marginTop: 12, padding: '10px 16px', background: 'rgba(52,211,153,0.1)',
                    border: '1px solid rgba(52,211,153,0.25)', borderRadius: 8,
                    color: '#34d399', fontSize: 13, fontWeight: 600,
                  }}>
                    &#10003; Number verified!
                  </div>
                )}
              </fieldset>

              <fieldset style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '12px 16px', margin: '0 0 12px' }}>
                <legend style={{ color: '#94a3b8', fontSize: 13, padding: '0 6px' }}>Call Forwarding</legend>
                <label>
                  Forward Calls To (optional)
                  <input
                    type="tel"
                    placeholder="+15559876543"
                    value={forwardNumber}
                    onChange={(e) => setForwardNumber(e.target.value)}
                    className="onboarding-input"
                  />
                  <small style={{ color: '#94a3b8', fontSize: 11 }}>
                    Your cell or desk phone. If set, it rings alongside the browser when you get a call.
                  </small>
                </label>
              </fieldset>

              {error && <div className="login-error">{error}</div>}

              <button type="submit" className="onboarding-btn" disabled={loading || !verified}>
                {loading ? 'Saving...' : 'Complete Setup'}
              </button>
            </form>
          </div>
        )}

        {step === 2 && (
          <div className="onboarding-body">
            <h2>Connect Your CRM</h2>
            <p style={{ marginBottom: 8, color: '#94a3b8' }}>
              Optionally connect GoHighLevel to enrich calls with customer data and push coaching summaries back.
            </p>

            <form onSubmit={handleCrmSubmit} className="onboarding-form">
              <fieldset style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '12px 16px', margin: '0 0 12px' }}>
                <legend style={{ color: '#94a3b8', fontSize: 13, padding: '0 6px' }}>GoHighLevel</legend>
                <label>
                  Private Integration Token
                  <input
                    type="password"
                    placeholder="pit-xxxxxxxxxxxxxxxxxxxxxxxx"
                    value={crmApiKey}
                    onChange={(e) => setCrmApiKey(e.target.value)}
                    required
                    className="onboarding-input"
                  />
                  <small style={{ color: '#94a3b8', fontSize: 11 }}>
                    Settings &rarr; Integrations &rarr; Private Integrations in GHL
                  </small>
                </label>
                <label style={{ marginTop: 12, display: 'block' }}>
                  Location ID
                  <input
                    type="text"
                    placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxx"
                    value={crmLocationId}
                    onChange={(e) => setCrmLocationId(e.target.value)}
                    required
                    className="onboarding-input"
                  />
                  <small style={{ color: '#94a3b8', fontSize: 11 }}>
                    Found in GHL sub-account settings or the URL after /location/
                  </small>
                </label>
              </fieldset>

              {crmError && <div className="login-error">{crmError}</div>}

              <button type="submit" className="onboarding-btn" disabled={crmSaving || !crmApiKey.trim() || !crmLocationId.trim()}>
                {crmSaving ? 'Connecting...' : 'Connect GoHighLevel'}
              </button>
            </form>

            <button
              type="button"
              onClick={() => setStep(3)}
              style={{
                display: 'block', margin: '12px auto 0', background: 'none',
                border: 'none', color: 'var(--mppc-text-body)', cursor: 'pointer',
                fontSize: 14, textDecoration: 'underline',
              }}
            >
              Skip — I'll set this up later in Settings
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="onboarding-body onboarding-done">
            <div className="done-icon">&#10003;</div>
            <h2>You're All Set!</h2>
            <p>Your AI call coach is ready. Make or receive a call to see it in action.</p>
            {platformNumber && (
              <div style={{
                padding: '12px 16px', background: 'rgba(99,102,241,0.1)',
                border: '1px solid rgba(99,102,241,0.25)', borderRadius: 8,
                margin: '12px 0', textAlign: 'center',
              }}>
                <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>Your coaching line</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', fontFamily: 'monospace' }}>
                  {platformNumber}
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                  Forward your business line here, or give this number to clients directly.
                </div>
              </div>
            )}
            <button className="onboarding-btn" onClick={onComplete}>
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
